import { selectFirstVenueId } from '@/features/user/selectors';
import { selectSessionToken } from '@/features/user/selectors';
import flippyService from '@/services/flippyService';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { keys } from 'ramda';

export const fetchFeatureFlag = createAsyncThunk(
  'featureFlags/fetchFeatureFlag',
  async ({ featureFlipName, sessionToken, venueId }, { rejectWithValue }) => {
    try {
      const payload = await flippyService.fetchFeatureFlag(featureFlipName, sessionToken, venueId);
      const result = { featureFlipName, enabled: payload[featureFlipName].data };
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetchFeatureFlags',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const sessionToken = selectSessionToken(state);
    const venueId = selectFirstVenueId(state);
    const flipNames = keys(state.featureFlags.features);

    if (!sessionToken || !venueId) {
      return rejectWithValue('Session token or venueId is missing');
    }

    const promises = flipNames.map((featureFlipName) =>
      dispatch(fetchFeatureFlag({ featureFlipName, sessionToken, venueId }))
    );
    return Promise.all(promises);
  }
);

const featureFlagSlice = createSlice({
  name: 'featureFlags',
  initialState: {
    features: {
      'enable-MFA-2025': { enabled: false, fetched: false, loading: false },
      'disable-skip-MFA-2025': { enabled: false, fetched: false, loading: false }
    },
    loading: false,
    hasLoaded: false,
    error: null
  },
  reducers: {
    setFeatureFlags(state, action) {
      state.featureFlags = action.payload;
    },
    setFeatureFlag(state, action) {
      const { featureFlipName, enabled } = action.payload;
      state.features[featureFlipName] = {
        ...state.features[featureFlipName],
        enabled,
        fetched: true,
        loading: false
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.hasLoaded = false;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state) => {
        state.loading = false;
        state.hasLoaded = true;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.hasLoaded = true;
      })
      .addCase(fetchFeatureFlag.pending, (state, action) => {
        const featureFlipName = action.meta.arg.featureFlipName;
        state.features[featureFlipName].loading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlag.fulfilled, (state, action) => {
        const { featureFlipName, enabled } = action.payload;
        state.features[featureFlipName] = {
          ...state.features[featureFlipName],
          enabled,
          fetched: true,
          loading: false
        };
      })
      .addCase(fetchFeatureFlag.rejected, (state, action) => {
        const featureFlipName = action.meta.arg.featureFlipName;
        state.features[featureFlipName].loading = false;
        state.error = action.error;
      });
  }
});

export const { setFeatureFlags } = featureFlagSlice.actions;
export default featureFlagSlice.reducer;
